import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Anchor,
  Stack,
  Alert,
  Center,
  Box,
  Group,
  Image,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { z } from 'zod';
import { useAuth } from '@/hooks/useAuth';

const registerSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  companyName: z.string().min(2, 'Company name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type RegisterFormValues = z.infer<typeof registerSchema>;

export function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<RegisterFormValues>({
    validate: zodResolver(registerSchema),
    initialValues: {
      firstName: '',
      lastName: '',
      companyName: '',
      email: '',
      password: '',
    },
  });

  const handleSubmit = async (values: RegisterFormValues) => {
    setError(null);
    setIsLoading(true);

    try {
      const fullName = `${values.firstName} ${values.lastName}`.trim();
      const result = await register(values.email, values.password, fullName, values.companyName);

      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <Box
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0b487c 0%, #1a6eb5 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
        }}
      >
        <Container size={420}>
          <Paper withBorder shadow="xl" p="xl" radius="lg">
            <Center>
              <IconCheck size={60} color="green" />
            </Center>
            <Title order={2} ta="center" mt="md" mb="md">
              You're all set!
            </Title>
            <Text c="dimmed" ta="center" mb="xl">
              Check your email to verify your account, then sign in to get started.
            </Text>
            <Button fullWidth size="md" component={Link} to="/login">
              Go to Login
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0b487c 0%, #1a6eb5 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <Container size={480}>
        <Center mb={40}>
          <Image src="/logo-inverse.png" alt="TrustPuma" h={90} w="auto" />
        </Center>

        <Paper withBorder shadow="xl" p={40} radius="lg">
          <Title order={2} ta="center" mb="md">
            Start your free trial
          </Title>
          <Text c="dimmed" size="sm" ta="center" mb="xl">
            No credit card required · 30 day trial
          </Text>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
              {error}
            </Alert>
          )}

          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <Group grow>
                <TextInput
                  label="First Name"
                  placeholder="John"
                  required
                  size="md"
                  {...form.getInputProps('firstName')}
                />
                <TextInput
                  label="Last Name"
                  placeholder="Doe"
                  required
                  size="md"
                  {...form.getInputProps('lastName')}
                />
              </Group>

              <TextInput
                label="Company Name"
                placeholder="Acme Corp"
                required
                size="md"
                {...form.getInputProps('companyName')}
              />

              <TextInput
                label="Email"
                placeholder="you@company.com"
                required
                size="md"
                {...form.getInputProps('email')}
              />

              <PasswordInput
                label="Password"
                placeholder="Min. 8 characters"
                required
                size="md"
                {...form.getInputProps('password')}
              />

              <Button type="submit" fullWidth size="md" mt="sm" loading={isLoading}>
                Create account
              </Button>
            </Stack>
          </form>

          <Text c="dimmed" size="sm" ta="center" mt="xl">
            Already have an account?{' '}
            <Anchor component={Link} to="/login" fw={500}>
              Sign in
            </Anchor>
          </Text>
        </Paper>
      </Container>
    </Box>
  );
}
